(() => {
  const appUrl = 'https://palacmalawies.premiumhotel.pl/';
  const supportedLocales = new Set(['pl', 'en', 'uk']);
  const documentLocale = (document.documentElement.lang || 'pl').toLowerCase().split('-')[0];
  const locale = supportedLocales.has(documentLocale) ? documentLocale : 'pl';
  const localeForDates = locale === 'uk' ? 'uk-UA' : (locale === 'en' ? 'en-GB' : 'pl-PL');

  const guestLabel = (count) => {
    if (locale === 'en') return count === 1 ? '1 guest' : count + ' guests';
    if (locale === 'uk') {
      if (count === 1) return '1 гість';
      if (count >= 2 && count <= 4) return count + ' гості';
      return count + ' гостей';
    }
    if (count === 1) return '1 osoba';
    if (count >= 2 && count <= 4) return count + ' osoby';
    return count + ' osób';
  };

  let activePopover = null;
  const pickerCopy = {
    pl: { guests: 'Liczba gości', done: 'Gotowe', previous: 'Poprzedni miesiąc', next: 'Następny miesiąc', calendar: 'Wybierz datę' },
    en: { guests: 'Number of guests', done: 'Done', previous: 'Previous month', next: 'Next month', calendar: 'Choose a date' },
    uk: { guests: 'Кількість гостей', done: 'Готово', previous: 'Попередній місяць', next: 'Наступний місяць', calendar: 'Виберіть дату' }
  };
  const uiCopy = pickerCopy[locale];

  const parseISODate = (value) => {
    if (!value) return null;
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    return new Date(parts[0], parts[1] - 1, parts[2], 12);
  };

  const toISODate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const closePopover = () => {
    activePopover?.element.remove();
    activePopover?.anchor.classList.remove('is-picker-open');
    activePopover = null;
  };

  const positionPopover = () => {
    if (!activePopover) return;
    const { anchor, element } = activePopover;
    const rect = anchor.getBoundingClientRect();
    const gap = 10;
    const edge = 12;
    const width = Math.min(element.offsetWidth, window.innerWidth - edge * 2);
    element.style.width = width + 'px';
    const left = Math.min(Math.max(rect.left, edge), window.innerWidth - width - edge);
    const roomBelow = window.innerHeight - rect.bottom;
    const top = roomBelow >= element.offsetHeight + gap
      ? rect.bottom + gap
      : Math.max(edge, rect.top - element.offsetHeight - gap);
    element.style.left = left + 'px';
    element.style.top = top + 'px';
  };

  const mountPopover = (anchor, element) => {
    closePopover();
    document.body.appendChild(element);
    anchor.classList.add('is-picker-open');
    activePopover = { anchor, element };
    requestAnimationFrame(positionPopover);
  };

  const openCalendar = (anchor, input) => {
    if (activePopover?.anchor === anchor) {
      closePopover();
      return;
    }
    const minimum = parseISODate(input.min);
    const selected = parseISODate(input.value);
    const initial = selected || minimum || new Date();
    let viewedMonth = new Date(initial.getFullYear(), initial.getMonth(), 1, 12);
    const popover = document.createElement('div');
    popover.className = 'pmw-booking-popover pmw-calendar';
    popover.setAttribute('role', 'dialog');
    popover.setAttribute('aria-label', uiCopy.calendar);
    popover.innerHTML = `
      <div class="pmw-calendar__header">
        <button type="button" data-calendar-prev aria-label="${uiCopy.previous}">←</button>
        <strong data-calendar-month></strong>
        <button type="button" data-calendar-next aria-label="${uiCopy.next}">→</button>
      </div>
      <div class="pmw-calendar__weekdays"></div>
      <div class="pmw-calendar__days"></div>`;
    const monthLabel = popover.querySelector('[data-calendar-month]');
    const weekdays = popover.querySelector('.pmw-calendar__weekdays');
    const days = popover.querySelector('.pmw-calendar__days');
    const previous = popover.querySelector('[data-calendar-prev]');
    const next = popover.querySelector('[data-calendar-next]');
    const monday = new Date(2024, 0, 1, 12);
    for (let index = 0; index < 7; index += 1) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + index);
      const label = document.createElement('span');
      label.textContent = new Intl.DateTimeFormat(localeForDates, { weekday: 'short' }).format(day).replace('.', '');
      weekdays.appendChild(label);
    }

    const render = () => {
      monthLabel.textContent = new Intl.DateTimeFormat(localeForDates, { month: 'long', year: 'numeric' }).format(viewedMonth);
      days.replaceChildren();
      const first = new Date(viewedMonth.getFullYear(), viewedMonth.getMonth(), 1, 12);
      const offset = (first.getDay() + 6) % 7;
      const daysInMonth = new Date(viewedMonth.getFullYear(), viewedMonth.getMonth() + 1, 0, 12).getDate();
      for (let index = 0; index < offset; index += 1) {
        const spacer = document.createElement('span');
        spacer.className = 'pmw-calendar__empty';
        days.appendChild(spacer);
      }
      const todayISO = toISODate(new Date());
      for (let number = 1; number <= daysInMonth; number += 1) {
        const date = new Date(viewedMonth.getFullYear(), viewedMonth.getMonth(), number, 12);
        const iso = toISODate(date);
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = String(number);
        button.dataset.calendarDate = iso;
        button.disabled = Boolean(input.min && iso < input.min);
        if (iso === input.value) button.classList.add('is-selected');
        if (iso === todayISO) button.classList.add('is-today');
        days.appendChild(button);
      }
      if (minimum) {
        const minimumMonth = new Date(minimum.getFullYear(), minimum.getMonth(), 1, 12);
        previous.disabled = viewedMonth <= minimumMonth;
      }
      requestAnimationFrame(positionPopover);
    };

    previous.addEventListener('click', () => {
      viewedMonth = new Date(viewedMonth.getFullYear(), viewedMonth.getMonth() - 1, 1, 12);
      render();
    });
    next.addEventListener('click', () => {
      viewedMonth = new Date(viewedMonth.getFullYear(), viewedMonth.getMonth() + 1, 1, 12);
      render();
    });
    days.addEventListener('click', (event) => {
      const day = event.target.closest('[data-calendar-date]');
      if (!day || day.disabled) return;
      input.value = day.dataset.calendarDate;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      closePopover();
    });
    render();
    mountPopover(anchor, popover);
  };

  const openGuestPicker = (anchor, select, display) => {
    if (activePopover?.anchor === anchor) {
      closePopover();
      return;
    }
    let count = Number(select.value || 2);
    const popover = document.createElement('div');
    popover.className = 'pmw-booking-popover pmw-guests-picker';
    popover.setAttribute('role', 'dialog');
    popover.setAttribute('aria-label', uiCopy.guests);
    popover.innerHTML = `
      <p>${uiCopy.guests}</p>
      <div class="pmw-guests-picker__stepper">
        <button type="button" data-guests-minus aria-label="−">−</button>
        <strong data-guests-count></strong>
        <button type="button" data-guests-plus aria-label="+">+</button>
      </div>
      <button type="button" class="pmw-guests-picker__done">${uiCopy.done}</button>`;
    const countDisplay = popover.querySelector('[data-guests-count]');
    const minus = popover.querySelector('[data-guests-minus]');
    const plus = popover.querySelector('[data-guests-plus]');
    const update = () => {
      select.value = String(count);
      display.textContent = guestLabel(count);
      countDisplay.textContent = guestLabel(count);
      minus.disabled = count <= 1;
      plus.disabled = count >= 8;
    };
    minus.addEventListener('click', () => {
      if (count > 1) count -= 1;
      update();
    });
    plus.addEventListener('click', () => {
      if (count < 8) count += 1;
      update();
    });
    popover.querySelector('.pmw-guests-picker__done').addEventListener('click', () => {
      select.dispatchEvent(new Event('change', { bubbles: true }));
      closePopover();
    });
    update();
    mountPopover(anchor, popover);
  };

  const acceptedCookies = () => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return [];
    const accepted = ['security_storage'];
    try {
      if (privacy.preferencesProcessingAllowed?.()) {
        accepted.push('functionality_storage', 'personalization_storage');
      }
      if (privacy.analyticsProcessingAllowed?.()) accepted.push('analytics_storage');
      if (privacy.marketingAllowed?.()) {
        accepted.push('ad_storage', 'ad_user_data', 'ad_personalization');
      }
    } catch (error) {
      return [];
    }
    return accepted;
  };

  const closeBookingFrame = () => {
    const overlay = document.getElementById('bph-overlay');
    if (!overlay) return;
    overlay.remove();
    document.body.style.removeProperty('overflow-y');
  };

  const openBooking = () => {
    closePopover();
    const booking = document.querySelector('[data-betasi-booking]');
    const checkIn = booking?.querySelector('[data-betasi-check-in]')?.value || '';
    const checkOut = booking?.querySelector('[data-betasi-check-out]')?.value || '';
    const adults = Number(booking?.querySelector('[data-betasi-adults]')?.value || 2);
    const configuration = {
      context: 'pmw',
      target: 'frame',
      module: 'booking',
      blankHeader: true,
      lang: locale,
      adults: Number.isFinite(adults) ? adults : 2
    };
    if (checkIn) configuration.checkIn = checkIn;
    if (checkOut) configuration.checkOut = checkOut;
    const cookies = acceptedCookies();
    if (cookies.length) configuration.acceptedCookies = cookies;

    if (window.BetasiBooking && typeof window.BetasiBooking.Open === 'function') {
      window.BetasiBooking.Open(configuration);
      return;
    }
    window.location.href = appUrl;
  };

  const formatDate = (value, fallback) => {
    if (!value) return fallback;
    const date = new Date(value + 'T12:00:00');
    if (Number.isNaN(date.getTime())) return fallback;
    return new Intl.DateTimeFormat(localeForDates, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  const addDays = (value, days) => {
    const date = new Date(value + 'T12:00:00');
    date.setDate(date.getDate() + days);
    return date.toISOString().slice(0, 10);
  };

  const initBooking = (root = document) => {
    const booking = root.querySelector?.('[data-betasi-booking]') || (root.matches?.('[data-betasi-booking]') ? root : null);
    if (booking && booking.dataset.betasiReady !== 'true') {
      booking.dataset.betasiReady = 'true';
      const checkIn = booking.querySelector('[data-betasi-check-in]');
      const checkOut = booking.querySelector('[data-betasi-check-out]');
      const today = new Date();
      const todayValue = new Date(today.getTime() - today.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      if (checkIn) checkIn.min = todayValue;
      if (checkOut) checkOut.min = todayValue;

      booking.querySelectorAll('[data-betasi-date]').forEach((field) => {
        const input = field.querySelector('input[type="date"]');
        const display = field.querySelector('[data-betasi-date-display]');
        const fallback = display?.dataset.placeholder || display?.textContent || '';
        const update = () => {
          if (display) display.textContent = formatDate(input.value, fallback);
          field.classList.toggle('has-value', Boolean(input.value));
        };
        input?.addEventListener('change', update);
        input?.setAttribute('tabindex', '-1');
        input?.setAttribute('aria-hidden', 'true');
        field.setAttribute('role', 'button');
        field.setAttribute('tabindex', '0');
        field.setAttribute('aria-haspopup', 'dialog');
        field.addEventListener('click', (event) => {
          event.preventDefault();
          openCalendar(field, input);
        });
        field.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          openCalendar(field, input);
        });
        update();
      });

      checkIn?.addEventListener('change', () => {
        if (!checkIn.value || !checkOut) return;
        const minimumCheckout = addDays(checkIn.value, 1);
        checkOut.min = minimumCheckout;
        if (checkOut.value && checkOut.value < minimumCheckout) {
          checkOut.value = minimumCheckout;
          checkOut.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      const adults = booking.querySelector('[data-betasi-adults]');
      if (adults) {
        [...adults.options].forEach((option) => {
          option.textContent = guestLabel(Number(option.value));
        });
        const guestsField = adults.closest('.booking__guests');
        const guestsDisplay = document.createElement('strong');
        guestsDisplay.className = 'booking__guests-display';
        guestsDisplay.textContent = guestLabel(Number(adults.value || 2));
        adults.before(guestsDisplay);
        adults.setAttribute('tabindex', '-1');
        adults.setAttribute('aria-hidden', 'true');
        guestsField?.setAttribute('role', 'button');
        guestsField?.setAttribute('tabindex', '0');
        guestsField?.setAttribute('aria-haspopup', 'dialog');
        const openGuests = (event) => {
          event.preventDefault();
          openGuestPicker(guestsField, adults, guestsDisplay);
        };
        guestsField?.addEventListener('click', openGuests);
        guestsField?.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          openGuests(event);
        });
      }
    }

    document.querySelectorAll('[data-betasi-open]').forEach((button) => {
      if (button.dataset.betasiOpenReady === 'true') return;
      button.dataset.betasiOpenReady = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        openBooking();
      });
    });

  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initBooking());
  } else {
    initBooking();
  }
  document.addEventListener('pointerdown', (event) => {
    if (!activePopover) return;
    if (activePopover.element.contains(event.target) || activePopover.anchor.contains(event.target)) return;
    closePopover();
  });
  window.addEventListener('resize', positionPopover);
  window.addEventListener('scroll', positionPopover, true);
  document.addEventListener('click', (event) => {
    if (!event.target.closest?.('#bph-frame-close button')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    closeBookingFrame();
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (activePopover) closePopover();
    else closeBookingFrame();
  });
  document.addEventListener('shopify:section:load', (event) => initBooking(event.target));
})();
